import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {TranslatorService} from '../../../../translation/translator.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {TeamUser} from '../../../../team/state/team-user';
import {BubbleScore, BubbleScoreFunctions} from '../../../../team/state/bubble-score';
import {Motive} from '../../../../report/types/motives/motive';
import {Layout} from '../../../../team/team.component';
import {Team} from '../../../../team/state/team';
import {TeamService} from '../../../../team/state/team.service';
import {Subscription} from 'rxjs';


@Component({
  selector: 'app-team-graphic-motives',
  templateUrl: './team-graphic-motives.component.html',
  styleUrls: ['./team-graphic-motives.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamGraphicMotivesComponent implements OnInit, OnDestroy {

  @Input() layout: Layout;

  @Output() orderChange = new EventEmitter<boolean>();

  teamId: string;
  _members: TeamUser[] = [];  // Initialize as empty array

  bubbleScores: BubbleScore[] = [];
  defaultMotives: string[] = Motive.STRING_LIST;
  displayingMotives: string[] = [];
  orderedMotives: string[] = [];  // Store complete list in current order
  maxNumberOfMotivesShown: number = 14;

  resetSub: Subscription;
  motivesVariationChangedSub: Subscription;

  constructor(private changeDetectionRef: ChangeDetectorRef,
              private teamService: TeamService,
              private translatorService: TranslatorService) {
    this.maxNumberOfMotivesShown = 14;
    // Initialize without FAM and SIN motives
    this.orderedMotives = this.defaultMotives.filter(m => m !== Motive.FAM && m !== Motive.SIN);
    this.displayingMotives = [...this.orderedMotives];
  }

  ngOnInit(): void {
    this.resetSub = this.teamService.resetView.subscribe(() => {
      // Reset to 14 motives without FAM and SIN
      this.maxNumberOfMotivesShown = 14;
      this.orderedMotives = this.defaultMotives.filter(m => m !== Motive.FAM && m !== Motive.SIN);
      this.displayingMotives = [...this.orderedMotives];
      this.changeDetectionRef.detectChanges();
    });

    this.motivesVariationChangedSub = this.teamService.motivesVariationChanged.subscribe((variation: number) => {
      if (variation > this.maxNumberOfMotivesShown) {
        // When increasing variation, add specific motives based on current variation
        if (variation === 15) {
          // Add FAM motive
          this.displayingMotives = [...this.displayingMotives, Motive.FAM];
        }
        else if (variation === 16) {
          // Add SIN motive
          this.displayingMotives = [...this.displayingMotives, Motive.SIN];
        }
      }
      else {
        // When decreasing, remove specific motives first
        if (variation === 15) {
          // Remove SIN motive
          this.displayingMotives = this.displayingMotives.filter(m => m !== Motive.SIN);
        }
        else if (variation === 14) {
          // Remove FAM motive
          this.displayingMotives = this.displayingMotives.filter(m => m !== Motive.FAM);
        }
      }
      this.maxNumberOfMotivesShown = variation;
      this.orderedMotives = [...this.displayingMotives];
      this.changeDetectionRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.resetSub) {
      this.resetSub.unsubscribe();
    }
    if (this.motivesVariationChangedSub) {
      this.motivesVariationChangedSub.unsubscribe();
    }
  }

  @Input()
  set members(team: Team) {
    if (!team) {
      this._members = [];
      return;
    }

    // Update members array and handle empty teams
    this._members = team.users || [];

    if (this._members.length === 0) {
      // For empty teams, just update the ID but don't trigger computations
      this.teamId = team.teamId;
      return;
    }

    // Handle team ID changes for non-empty teams
    if (!this.teamId || this.teamId !== team.teamId) {
      this.teamId = team.teamId;
      this.maxNumberOfMotivesShown = 14;
      this.computeMotiveGraphics();
    }
  }

  computeMotiveGraphics(): void {
    // Initialize with default motives except FAM and SIN
    this.orderedMotives = this.defaultMotives.filter(m => m !== Motive.FAM && m !== Motive.SIN);
    // Add FAM and SIN at the end if needed
    if (this.maxNumberOfMotivesShown >= 15) {
      this.orderedMotives.push(Motive.FAM);
    }
    if (this.maxNumberOfMotivesShown === 16) {
      this.orderedMotives.push(Motive.SIN);
    }
    this.displayingMotives = [...this.orderedMotives];
    this.changeDetectionRef.markForCheck();
  }

  // Keep track of motive order changes
  private updateMotiveOrder(): void {
    this.orderedMotives = [...this.displayingMotives];
  }


  // return BubbleScore for a certain motive
  getSpecificBubbleScores(motiveName: string): BubbleScore[] | undefined {
    return BubbleScoreFunctions.getSpecificBubbleScores(this._members, motiveName);
  }

  __(key: string): string {
    return this.translatorService.translate('motive.team.' + key.toLowerCase());
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      this.orderChange.emit(true);
      moveItemInArray(this.displayingMotives, event.previousIndex, event.currentIndex);
      // Update ordered motives when order changes
      this.updateMotiveOrder();
    }
  }

  getLayoutClass(): string {
    if (this.layout === 'desktop') {
      return 'drag-handle-desktop';
    }
    else if (this.layout === 'tablet') {
      return 'drag-handle-tablet';
    }
    else if (this.layout === 'mobile') {
      return 'drag-handle-mobile';
    }
  }

  hasMotiveEros(motive: string): boolean {
    return motive === Motive.SIN;
  }
}
